import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Spinner, Modal, Button } from 'react-bootstrap';

const DeveloperFormLayer = () => {
    const [formData, setFormData] = useState({
        name: '',
        logo: '', // Base64 string
        contactNumber: '',
        email: '',
        address: '',
        // iban: '', // Bank details commented out
        // swiftCode: '', // Bank details commented out
        // nameOfAccount: '', // Bank details commented out
        // bankName: '', // Bank details commented out
        // branchNumber: '', // Bank details commented out
        // bankAddress: '', // Bank details commented out
        segelName: '',
        segelNumber: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState([]);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [logoFileName, setLogoFileName] = useState('');
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const navigate = useNavigate();

    // List of personal email domains to reject
    const personalEmailDomains = [
        'brokerz.com','gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
        'aol.com', 'live.com', 'msn.com', 'yahoo.co.uk', 'googlemail.com',
        'me.com', 'mac.com', 'ymail.com', 'rocketmail.com', 'mail.com'
    ];

    // Helper function to determine if field has error
    const hasFieldError = (fieldName) => {
        return validationErrors.some(error => 
            error.toLowerCase().includes(fieldName.toLowerCase())
        );
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Clear validation errors when user starts typing
        if (validationErrors.length > 0) {
            setValidationErrors([]);
        }
        
        // Special validation for email field
        if (name === 'email' && value) {
            const emailDomain = value.split('@')[1]?.toLowerCase();
            if (emailDomain && personalEmailDomains.includes(emailDomain)) {
                setError(['Please use a business email address. Personal email domains like Gmail, Yahoo, Hotmail are not allowed.']);
            } else {
                // Clear email-related errors if email is valid
                setError(prevErrors => prevErrors.filter(err => 
                    !err.includes('business email') && !err.includes('Personal email domains')
                ));
            }
        }
        
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle file input and convert to Base64
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const maxSize = 2 * 1024 * 1024; // 2MB

        if (file && file.size > maxSize) {
            setError(['File size exceeds 2MB.']);
            // Clear the file input to prevent submission
            e.target.value = null; // VERY IMPORTANT:  Reset the file input
            setLogoFileName('');
        } else if (file) {
            setError([]); //Clear Previous Error if file valid
            setLogoFileName(file.name); // Store the filename for display
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData((prev) => ({ ...prev, logo: reader.result }));
            };
            reader.readAsDataURL(file);
        } else {
            // File was cleared
            setLogoFileName('');
            setFormData((prev) => ({ ...prev, logo: '' }));
        }
    };

    // Comprehensive form validation
    const validateForm = () => {
        const errors = [];
        
        // Required field validations
        if (!formData.name.trim()) {
            errors.push('Developer Name is required');
        }
        
        if (!formData.email.trim()) {
            errors.push('Business Email is required');
        } else {
            // Email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                errors.push('Please enter a valid email address');
            } else {
                // Business email validation
                const emailDomain = formData.email.split('@')[1]?.toLowerCase();
                if (emailDomain && personalEmailDomains.includes(emailDomain)) {
                    errors.push('Please use a business email address. Personal email domains like Gmail, Yahoo, Hotmail are not allowed');
                }
            }
        }
        
        if (!formData.contactNumber.trim()) {
            errors.push('Contact Number is required');
        } else {
            // Phone number validation (basic)
            const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/;
            if (!phoneRegex.test(formData.contactNumber.trim())) {
                errors.push('Please enter a valid contact number');
            }
        }
        
        if (!formData.segelName.trim()) {
            errors.push('Segel Name is required');
        }
        
        if (!formData.segelNumber.trim()) {
            errors.push('Tax Number is required');
        }
        
        if (!formData.logo) {
            errors.push('Developer Logo is required');
        }
        
        return errors;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Perform comprehensive validation
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setValidationErrors(validationErrors);
            setShowValidationModal(true);
            return;
        }
        
        setLoading(true);
        setError([]); // Clear previous errors

        // Create a payload without the bank details
        const payload = {
            name: formData.name.trim(),
            logo: formData.logo,
            contactNumber: formData.contactNumber.trim(),
            email: formData.email.trim().toLowerCase(),
            address: formData.address.trim(),
            segelName: formData.segelName.trim(),
            segelNumber: formData.segelNumber.trim(),
            // Explicitly exclude bank details from being sent
        };


        try {
            // Send the modified payload
            const response = await api.post('/developers/register', payload);
            if (response.data.status) {
                setSubmissionSuccess(true);
                toast.success('Developer submitted for approval.');
                // Don't navigate away, show success state instead
            }
        }
        catch (err) {
            if (err.response && err.response.data) {
                const data = err.response.data;

                if (data.response) {
                    // Prioritize errors within the 'response' object.
                    const errorDetails = Object.values(data.response).flat();
                    if (errorDetails.length > 0) {
                        setError(errorDetails);
                        setLoading(false); // Stop loading here
                        return; // Important: Exit early if we found 'response' errors.
                    }
                }

                if (data.message) {
                    setError([data.message]);
                } else {
                    setError(['An unexpected error occurred.']);
                }
            } else {
                setError(['An error occurred.  Please check your network connection.']);
            }
            setLoading(false); // Stop loading in the catch block
        }
        finally {
            setLoading(false); // Ensure loading stops even if there's an unexpected error
        }
    };

    return (
        <section className="auth bg-base d-flex flex-wrap">
            {/* Left Side (Image) */}
            <div className="auth-left d-lg-block d-none">
                <div className="d-flex align-items-center flex-column h-100 justify-content-center">
                    <img
                        src="assets/images/auth/3.jpg"
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>
            </div>

            {/* Right Side (Form or Success Message) */}
            <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
                <div className="max-w-464-px mx-auto w-100">
                    <Link to="/" className="mb-40 max-w-290-px">
                        <h3>Brokerz</h3>
                    </Link>

                    {submissionSuccess ? (
                        // Success View
                        <div className="text-center">
                            <div className="mb-32">
                                <div className="d-flex justify-content-center mb-24">
                                    <div 
                                        className="bg-success-100 rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: '100px', height: '100px' }}
                                    >
                                        <Icon 
                                            icon="material-symbols:check-circle" 
                                            className="text-success-600" 
                                            style={{ fontSize: '60px' }} 
                                        />
                                    </div>
                                </div>
                                <h4 className="mb-16 text-success-600">Application Submitted Successfully!</h4>
                                <p className="text-neutral-700 mb-24 lh-lg">
                                    Thank you for registering as a developer with <strong>Brokerz</strong>. 
                                    Your application has been submitted and is now under review.
                                </p>
                                <div className="bg-primary-50 rounded-12 p-20 mb-24 text-start">
                                    <h6 className="text-primary-600 mb-12 d-flex align-items-center">
                                        <Icon icon="material-symbols:info" className="me-2" />
                                        What happens next?
                                    </h6>
                                    <ul className="text-neutral-700 mb-0 ps-3">
                                        <li className="mb-8">Our team will review your application</li>
                                        <li className="mb-8">You'll receive an email confirmation once approved</li>
                                        <li className="mb-8">After approval, you can signup your developer sales account to access dashboard</li>
                                    </ul>
                                </div>
                                <div className="d-flex flex-column flex-sm-row gap-3">
                                    <button
                                        onClick={() => {
                                            setSubmissionSuccess(false);
                                            setFormData({
                                                name: '', logo: '', contactNumber: '', email: '', address: '',
                                                segelName: '', segelNumber: '',
                                            });
                                            setLogoFileName('');
                                        }}
                                        className="btn btn-outline-primary px-24 py-12 radius-12"
                                    >
                                        <Icon icon="material-symbols:add" className="me-2" />
                                        Submit Another Application
                                    </button>
                                    <Link 
                                        to="/" 
                                        className="btn btn-primary px-24 py-12 radius-12"
                                    >
                                        <Icon icon="material-symbols:home" className="me-2" />
                                        Go to Homepage
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Original Form View
                        <>
                            <h6 className="mb-12">Register as a Developer</h6>
                            <p>
                                <span className="text-danger-600">
                                    Fields with * are required.
                                </span>
                            </p>
                    {error.length > 0 && (
                        <ul className="text-danger mb-16">
                            {error.map((msg, index) => (
                                <li key={index}>{msg}</li>
                            ))}
                        </ul>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* Company Name (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="f7:person" />
                            </span>
                            <input
                                type="text"
                                name="name"
                                placeholder="Developer Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('developer name') ? 'border-danger' : ''}`}
                                required
                            />
                        </div>

                        {/* Business Email (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mage:email" />
                            </span>
                            <input
                                type="email"
                                name="email"
                                placeholder="Business Email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('email') ? 'border-danger' : ''}`}
                                required
                            />
                        </div>

                        {/* Contact Number (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mage:phone" />
                            </span>
                            <input
                                type="tel"
                                name="contactNumber"
                                placeholder="Contact Number"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('contact number') ? 'border-danger' : ''}`}
                                required
                            />
                        </div>

                        {/* Company Address (Optional) */}
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:address-marker" />
                            </span>
                            <input
                                type="text"
                                name="address"
                                placeholder="Developer Address"
                                value={formData.address}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        {/* --- BANK DETAILS SECTION START (COMMENTED OUT) --- */}
                        {/*
                        // Account Name (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:account" />
                            </span>
                            <input
                                type="text"
                                name="nameOfAccount"
                                placeholder="Bank Account Holder Name"
                                value={formData.nameOfAccount}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        // Bank Name (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="bankName"
                                placeholder="Bank Name"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        // IBAN (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="iban"
                                placeholder="IBAN"
                                value={formData.iban}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                                pattern="^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$"
                                title="Enter a valid IBAN (e.g., GB29NWBK60161331926819)"
                            />
                        </div>


                        // Swift Code (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank-transfer" />
                            </span>
                            <input
                                type="text"
                                name="swiftCode"
                                placeholder="Swift Code"
                                value={formData.swiftCode}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        // Branch Number (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="branchNumber"
                                placeholder="Branch Number"
                                value={formData.branchNumber}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        // Bank Address (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:map-marker" />
                            </span>
                            <input
                                type="text"
                                name="bankAddress"
                                placeholder="Bank Address"
                                value={formData.bankAddress}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>
                         */}
                        {/* --- BANK DETAILS SECTION END (COMMENTED OUT) --- */}


                        {/* Segel Name (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:badge" />
                            </span>
                            <input
                                type="text"
                                name="segelName"
                                placeholder="Segel Name"
                                value={formData.segelName}
                                onChange={handleInputChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('segel name') ? 'border-danger' : ''}`}
                                required
                            />
                        </div>

                        {/* Segel Number (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:id-card" />
                            </span>
                            <input
                                type="text"
                                name="segelNumber"
                                placeholder="Tax Number"
                                value={formData.segelNumber}
                                onChange={handleInputChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('tax number') ? 'border-danger' : ''}`}
                                required
                            />
                        </div>

                        {/* Logo Upload (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <label className="form-label text-neutral-900 fw-medium mb-8">
                               Developer Logo <span className="text-danger">*</span>
                            </label>
                            
                            {/* Sample Logo Preview */}
                            <div className="d-flex align-items-center mb-12">
                                <div className="bg-neutral-100 border rounded me-12" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {formData.logo ? (
                                        <img 
                                            src={formData.logo} 
                                            alt="Developer Logo Preview" 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <Icon icon="mdi:office-building" className="text-neutral-400" style={{ fontSize: '24px' }} />
                                    )}
                                </div>
                                <div>
                                    <small className="text-neutral-700 fw-medium d-block">
                                        {logoFileName ? `Selected: ${logoFileName}` : 'Example: Developer Logo'}
                                    </small>
                                    <small className="text-neutral-500">Upload your developer logo (NOT tax documents)</small>
                                </div>
                            </div>

                            <input
                                type="file"
                                name="logo"
                                accept=".png, .jpg, .jpeg, image/png, image/jpeg, image/jpg"
                                onChange={handleFileChange}
                                className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('logo') ? 'border-danger' : ''}`}
                                required
                            />
                            <div className="mt-8">
                                <small className="text-secondary-light d-block">
                                    ✅ Upload your company/business logo (PNG, JPG) max 2MB
                                </small>
                                <small className="text-danger-600 d-block">
                                    ❌ Do NOT upload tax documents, certificates, or ID cards here
                                </small>
                                {formData.logo && (
                                    <small className="text-success-600 d-block mt-2">
                                        ✅ Logo uploaded successfully
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32 d-flex align-items-center justify-content-center"
                            disabled={loading}
                            style={{ minHeight: '56px' }}
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Icon icon="material-symbols:send" className="me-2" />
                                    Submit Application
                                </>
                            )}
                        </button>
                        <div className="mt-32 text-center text-sm">
                            <p className="mb-0">
                                <Link to="/" className="text-secondary-light fw-medium">
                                    <Icon icon="mdi:arrow-left" className="me-1" />
                                    Back to Home
                                </Link>
                            </p>
                        </div>
                    </form>
                        </>
                    )}
                </div>
            </div>

            {/* Validation Error Modal */}
            <Modal 
                show={showValidationModal} 
                onHide={() => setShowValidationModal(false)}
                centered
                className="validation-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-danger-600 d-flex align-items-center">
                        <Icon icon="material-symbols:warning" className="me-2" style={{ fontSize: '24px' }} />
                        Please Complete Required Fields
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="mb-3 text-neutral-700">
                        Please fix the following issues before submitting:
                    </p>
                    <ul className="text-danger-600 mb-0 ps-3">
                        {validationErrors.map((error, index) => (
                            <li key={index} className="mb-2">{error}</li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-2">
                    <Button 
                        variant="primary" 
                        onClick={() => setShowValidationModal(false)}
                        className="px-4"
                    >
                        Got it, I'll fix these
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Inline CSS for the red star pseudo-element */}
            <style>{`
        /* This ensures each required field .required-field has a red star inside the input */
        .required-field::after {
          content: "*";
          color: red;
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }
        
        /* Responsive modal styles */
        .validation-modal .modal-dialog {
          margin: 1rem;
        }
        
        /* Success view responsive styles */
        @media (max-width: 576px) {
          .validation-modal .modal-dialog {
            margin: 0.5rem;
          }
          
          .validation-modal .modal-body {
            padding: 1rem;
          }
          
          .validation-modal .modal-header {
            padding: 1rem 1rem 0.5rem 1rem;
          }
          
          .validation-modal .modal-footer {
            padding: 0.5rem 1rem 1rem 1rem;
          }
          
          .auth-right {
            padding: 2rem 1.5rem !important;
          }
          
          .max-w-464-px {
            padding: 0 0.5rem;
          }
          
          .d-flex.flex-column.flex-sm-row {
            flex-direction: column !important;
          }
          
          .d-flex.flex-column.flex-sm-row .btn {
            margin-bottom: 0.5rem;
          }
        }
      `}</style>
        </section>
    );
};

export default DeveloperFormLayer;